import React from "react";
import {
  Box,
  LinearProgress,
  Skeleton,
  Stack,
  Typography,
  keyframes,
  useMediaQuery,
  useTheme,
} from "@mui/material";

const pulse = keyframes`
  0%, 100% {
    opacity: 0.48;
    transform: scale(0.92);
  }
  50% {
    opacity: 0.9;
    transform: scale(1);
  }
`;

const orbit = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

type LoadingSize = "compact" | "standard" | "large";

const sizeMap: Record<LoadingSize, { mark: number; dot: number; fontSize: string }> = {
  compact: { mark: 28, dot: 5, fontSize: "0.75rem" },
  standard: { mark: 44, dot: 8, fontSize: "0.875rem" },
  large: { mark: 56, dot: 10, fontSize: "1rem" },
};

const loadingColor = {
  dark: "hsla(170, 42%, 74%, 0.84)",
  main: "hsla(170, 48%, 46%, 0.8)",
  light: "hsla(170, 54%, 88%, 0.78)",
  border: "hsla(170, 44%, 56%, 0.34)",
};

export function LoadingMark({
  size = "standard",
  showWordmark = true,
}: {
  size?: LoadingSize;
  showWordmark?: boolean;
}) {
  const reduceMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const dimensions = sizeMap[size];

  return (
    <Stack alignItems="center" spacing={size === "compact" ? 0.5 : 0.75} aria-hidden="true">
      <Box
        sx={{
          width: dimensions.mark,
          height: dimensions.mark,
          borderRadius: "50%",
          position: "relative",
          display: "grid",
          placeItems: "center",
          border: `1px solid ${loadingColor.border}`,
          background:
            "radial-gradient(circle at 50% 50%, hsla(170, 60%, 92%, 0.52), hsla(170, 48%, 78%, 0.16) 58%, transparent 60%)",
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 4,
            borderRadius: "50%",
            border: `2px solid ${loadingColor.light}`,
            borderTopColor: loadingColor.main,
            animation: reduceMotion ? "none" : `${orbit} 1100ms linear infinite`,
          },
          "&::after": {
            content: '""',
            width: dimensions.dot,
            height: dimensions.dot,
            borderRadius: "50%",
            backgroundColor: loadingColor.dark,
            boxShadow: `0 0 0 ${Math.max(3, dimensions.dot - 2)}px hsla(170, 42%, 80%, 0.16)`,
            animation: reduceMotion ? "none" : `${pulse} 1400ms ease-in-out infinite`,
          },
        }}
      />
      {showWordmark && (
        <Typography
          component="span"
          sx={{
            color: loadingColor.dark,
            fontSize: dimensions.fontSize,
            fontWeight: 600,
            letterSpacing: 0,
            lineHeight: 1,
            opacity: 0.72,
          }}
        >
          GreenSparks
        </Typography>
      )}
    </Stack>
  );
}

export function LoadingState({
  label,
  size = "standard",
  minHeight = 240,
  showWordmark = true,
}: {
  label?: string;
  size?: LoadingSize;
  minHeight?: number | string;
  showWordmark?: boolean;
}) {
  return (
    <Box
      role="status"
      aria-busy="true"
      aria-live="polite"
      sx={{
        width: "100%",
        minHeight,
        flex: 1,
        minWidth: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
    >
      <Stack alignItems="center" spacing={1.25}>
        <LoadingMark size={size} showWordmark={showWordmark} />
        {label && (
          <Typography variant="body2" sx={{ color: "text.secondary", textAlign: "center" }}>
            {label}
          </Typography>
        )}
      </Stack>
    </Box>
  );
}

export function InlineLoading({
  label,
  size = "compact",
}: {
  label?: string;
  size?: LoadingSize;
}) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.75}
      role="status"
      aria-busy="true"
      aria-live="polite"
      sx={{ minWidth: 0 }}
    >
      <LoadingMark size={size} showWordmark={false} />
      {label && (
        <Typography variant="caption" sx={{ color: "text.secondary", whiteSpace: "nowrap" }}>
          {label}
        </Typography>
      )}
    </Stack>
  );
}

export function ButtonLoadingLabel({ label }: { label: string }) {
  return (
    <Stack direction="row" alignItems="center" spacing={0.75} component="span">
      <LoadingMark size="compact" showWordmark={false} />
      <span>{label}</span>
    </Stack>
  );
}

export function ProgressState({
  label,
  value,
  minHeight = 120,
}: {
  label?: string;
  value?: number;
  minHeight?: number | string;
}) {
  const theme = useTheme();
  const normalizedValue = typeof value === "number" ? Math.max(0, Math.min(100, value)) : undefined;

  return (
    <Box
      role="status"
      aria-busy="true"
      aria-live="polite"
      sx={{
        minHeight,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
    >
      <Stack spacing={1.25} alignItems="center" sx={{ width: "min(100%, 360px)" }}>
        <LoadingMark size="standard" />
        {label && (
          <Typography variant="body2" sx={{ color: "text.secondary", textAlign: "center" }}>
            {label}
          </Typography>
        )}
        <LinearProgress
          variant={normalizedValue === undefined ? "indeterminate" : "determinate"}
          value={normalizedValue}
          sx={{
            width: "100%",
            height: 6,
            borderRadius: 999,
            bgcolor: theme.palette.mode === "dark" ? "hsla(170, 32%, 24%, 0.52)" : "hsla(170, 40%, 88%, 0.8)",
            "& .MuiLinearProgress-bar": {
              bgcolor: loadingColor.main,
            },
          }}
        />
        {normalizedValue !== undefined && (
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {Math.round(normalizedValue)}%
          </Typography>
        )}
      </Stack>
    </Box>
  );
}

export function SkeletonRows({
  rows = 5,
  minHeight = 220,
  maxWidth,
  framed = false,
  leftStructure = false,
}: {
  rows?: number;
  minHeight?: number | string;
  maxWidth?: number | string;
  framed?: boolean;
  leftStructure?: boolean;
}) {
  if (leftStructure) {
    return (
      <Box
        role="status"
        aria-busy="true"
        aria-live="polite"
        sx={{
          minHeight,
          width: "100%",
          maxWidth,
          p: 0,
          border: "1px solid var(--selection-border)",
          bgcolor: "background.paper",
          overflow: "hidden",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: "6px", p: "6px 8px" }}>
          {Array.from({ length: rows }).map((_, index) => (
            <Box
              key={index}
              sx={{
                border: "1px solid var(--selection-border)",
                p: "var(--density-table-cell-padding-block) var(--density-table-cell-padding-inline)",
              }}
            >
              <Skeleton
                variant="text"
                width={index % 3 === 0 ? "72%" : "58%"}
                sx={{ bgcolor: "hsla(170, 45%, 76%, 0.26)", fontSize: "var(--density-headline-font-size)" }}
              />
              <Skeleton
                variant="text"
                width={index % 2 === 0 ? "46%" : "34%"}
                sx={{ bgcolor: "hsla(170, 45%, 76%, 0.18)", fontSize: "var(--density-caption-font-size)" }}
              />
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      role="status"
      aria-busy="true"
      aria-live="polite"
      sx={{
        minHeight,
        width: "100%",
        maxWidth,
        p: 2,
        border: framed ? "1px solid var(--selection-border)" : undefined,
        bgcolor: framed ? "background.paper" : undefined,
      }}
    >
      <Stack spacing={1.25}>
        {Array.from({ length: rows }).map((_, index) => (
          <Box key={index} sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            <Skeleton
              variant="circular"
              width={12}
              height={12}
              sx={{ bgcolor: "hsla(170, 45%, 76%, 0.28)" }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Skeleton
                variant="text"
                width={index % 3 === 0 ? "72%" : "54%"}
                sx={{ bgcolor: "hsla(170, 45%, 76%, 0.26)", fontSize: "1rem" }}
              />
              <Skeleton
                variant="text"
                width={index % 2 === 0 ? "46%" : "34%"}
                sx={{ bgcolor: "hsla(170, 45%, 76%, 0.18)", fontSize: "0.75rem" }}
              />
            </Box>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

export function ContentStateFrame({ children }: { children: React.ReactNode }) {
  return (
    <Box sx={{ width: "100%", minHeight: 0, flex: 1, display: "flex", flexDirection: "column" }}>
      {children}
    </Box>
  );
}
